// TODO : split in https://sh4res.atlassian.net/browse/BRAV-8245
/* eslint-disable max-lines */
import { G } from '@mobily/ts-belt';
import { endOfDay, subYears } from 'date-fns';
import { z } from 'zod';

import { EMPLOYEE_MIN_LEGAL_WORK_AGE } from '@Common/constants/b2b/employees';
import { ZodAmountRequest } from '@Common/types/Amount';
import { ZodGetEmployeeBalanceResponse } from '@Common/types/b2b/balance';
import {
  B2bSavingPlanCategory,
  B2bSavingPlanType,
  ZodB2bSavingPlanCategory,
} from '@Common/types/b2b/plans';
import {
  ZodGetEmployeeSavingsPortfolioQuery,
  ZodGetEmployeeSavingsPortfolioResponse,
} from '@Common/types/b2b/portfolio';
import { ZodB2bCashSource } from '@Common/types/bo/b2b/b2b';
import { ZodB2bCompanyInstrumentsResponse, ZodKybStatus } from '@Common/types/bo/b2b/companies';
import { ZodB2bContractType } from '@Common/types/bo/b2b/contracts/contract_type';
import {
  ZodEmployeeStatus,
  ZodEmployeeTaxNumber,
  ZodLeavingReason,
  ZodSocialStatus,
} from '@Common/types/bo/b2b/employees_properties';
import { B2bCompanyPlanTypeParameters } from '@Common/types/bo/b2b/plans';
import { BoListQueryBase } from '@Common/types/bo/bo_list_query';
import { ZodUserDocumentSource } from '@Common/types/bo/document_source';
import { ZodReviewedRequestState } from '@Common/types/bo/reviewed_requests';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { ZodEmployeeSavingsMatchingContribution } from '@Common/types/explore/employee_savings';
import {
  ZodAmountInDays,
  ZodB2bCampaignAllocationDayType,
} from '@Common/types/explore/employee_savings_campaign';
import { ZodLanguageCode } from '@Common/types/language';
import {
  EmployeeSavingsRiskProfile,
  ZodEmployeeSavingsRiskProfile,
} from '@Common/types/profile/employee_savings';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { ZodCountryCode } from '@Common/types/zod/zod-country-code';
import { createZodDateLike, ZodDateLike } from '@Common/types/zod/zod-date-like';
import { ZodDateTimeString } from '@Common/types/zod/zod-date-string';
import { ZodSecureEmail } from '@Common/types/zod/zod-email';
import {
  // @ts-expect-error used once compiled by TSC, which significantly improves performance
  type EndpointConfig,
  endpoint,
  GetEndpointType,
} from '@Common/types/zod/zod-endpoint';
import { ZodNIR } from '@Common/types/zod/zod-nir';
import { ZodMobilePhoneNumber } from '@Common/types/zod/zod-phone-number';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

// @ts-expect-error used once compiled by TSC, which significantly improves performance
import type { SupportedCountryCodes } from '@Common/countries/country_data';

export const B2bEmployeeEditFields = [
  // user fields
  'city',
  'date_of_birth',
  'firstname',
  'gender',
  'lastname',
  'nationality',
  'phone_number',
  'place_of_birth',
  'postalcode',
  'street',
  'country',
  'nir',
  'ntt',
  'postcode_of_birth',

  // users session fields
  'email',
  'tax_number',
  'secondary_email',

  // employee fields
  'tax_residence_code',
  'role_in_company',
  'retirement_date',
  'country_of_birth',
  'language',
  'name_in_use',
  'contract_ids',
  'registration_number',

  // working relationship fields
  'status',
  'leaving_reason',
  'work_ending_date',
  'work_starting_date',
  'social_status',
  'csp_code',

  // external identifiers
  'id_external_taiga',
] as const satisfies (keyof PostCreateB2bEmployeeRequest | 'id_external_taiga')[];

export const ZodB2bEmployeeEditField = z.enum(B2bEmployeeEditFields);

export type B2bEmployeeEditField = z.infer<typeof ZodB2bEmployeeEditField>;

export const ZodDateOfBirth = createZodDateLike(z.date()).refine(
  (val) => {
    const oneYearAgo = endOfDay(subYears(new Date(), EMPLOYEE_MIN_LEGAL_WORK_AGE));
    return endOfDay(val) <= oneYearAgo;
  },
  {
    error: 'Employee age must be higher than zero (min_legal_age)',
  },
);

export const ZodTaxResidenceCode = z.enum(['tax_resident', 'non_tax_resident', 'resident_exempt']);
export type TaxResidenceCode = z.infer<typeof ZodTaxResidenceCode>;

export const EmployeeGenderValues = ['male', 'female'] as const;
export type EmployeeGender = (typeof EmployeeGenderValues)[number];
export const ZodEmployeeGender = z.enum(EmployeeGenderValues);

export const EmployeeAccountStatusValues = ['active', 'closed'] as const;
export type EmployeeAccountStatus = (typeof EmployeeAccountStatusValues)[number];
export const ZodEmployeeAccountStatus = z.enum(EmployeeAccountStatusValues);

export const EmployeeEckertStatusValues = [
  'not_activated',
  'active',
  'inactive',
  'desherence',
  'attente_depose_cdc',
  'depose_cdc_pee',
  'depose_cdc',
] as const;
export type EmployeeEckertStatus = (typeof EmployeeEckertStatusValues)[number];
export const ZodEmployeeEckertStatus = z.enum(EmployeeEckertStatusValues);

const ZodWorkStartingDate = createZodDateLike(z.date()).refine(
  (val) => val <= new Date(),
  'Work starting date cannot be in the future (work_starting_date_max)',
);

// Zod forbids `.pick()`/`.omit()`/`.partial()` on refined objects, so derive from this one
export const ZodGetB2bEmployeesOneResponseUnrefined = z.object({
  id: ZodId,
  company_id: ZodId,
  company_name: ZodTrimmedString,
  user_id: ZodId,
  firstname: ZodTrimmedString,
  lastname: ZodTrimmedString,
  email: ZodSecureEmail.nullable(),
  email_state: z.enum(['blocked', 'permanently_blocked', 'invalid']).nullable(),
  phone_number: ZodTrimmedString.nullable(),
  secondary_email: ZodTrimmedString.nullable(),
  secondary_email_state: z.enum(['blocked', 'permanently_blocked', 'invalid']).nullable(),
  street: ZodTrimmedString.nullable(),
  postalcode: ZodTrimmedString,
  city: ZodTrimmedString.nullable(),
  country: ZodCountryCode.nullable(),
  language: ZodLanguageCode,
  status: ZodEmployeeStatus,
  created_at: z.date(),
  updated_at: z.date(),
  date_of_birth: ZodDateLike.nullable(),
  country_of_birth: ZodCountryCode.nullable(),
  place_of_birth: ZodTrimmedString.nullable(),
  postcode_of_birth: ZodTrimmedString.nullable(),
  nationality: ZodCountryCode.nullable(),
  tax_number: ZodTrimmedString.nullable(),
  work_starting_date: ZodWorkStartingDate.nullable(),
  work_ending_date: ZodDateLike.nullable(),
  leaving_reason: ZodLeavingReason.nullable(),
  social_status: ZodSocialStatus,
  kyb_status: ZodKybStatus,
  net_employee_deposits: z.number().nonnegative().nullable(),
  net_employer_contributions: z.number().nonnegative().nullable(),
  retirement_date: ZodDateLike,
  tax_residence_code: ZodTaxResidenceCode,
  nir: ZodNIR.nullable(),
  ntt: ZodTrimmedString.nullable(),
  role_in_company: ZodTrimmedString.nullable(),
  registration_number: ZodTrimmedString.nullable(),
  name_in_use: ZodTrimmedString.nullable(),
  gender: ZodEmployeeGender.nullable(),
  account_status: ZodEmployeeAccountStatus,
  eckert_status: ZodEmployeeEckertStatus,
  eckert_status_changed_at: ZodDateLike.nullable(),
  last_activity_at: ZodDateLike.nullable(),
  account_closed_at: ZodDateLike.nullable(),
  account_closed_reason: ZodTrimmedString.nullable(),
  approbation_state: ZodTrimmedString.nullable(),
  is_phone_number_user_controlled: z.boolean(),
  is_email_user_controlled: z.boolean(),
  is_mailing_address_user_controlled: z.boolean(),
  is_address_user_controlled: z.boolean(),
  is_retirement_age_user_controlled: z.boolean(),
  is_deceased: z.boolean(),
  is_activated_by_company: z.boolean(),
  activation_code_is_used: z.boolean(),
  last_invitation_sent_at: ZodDateLike.nullable(),
  has_activation_code: z.boolean(),
  activation_code_expires_at: z.date().nullable(),
  activation_code_is_external: z.boolean(),
  has_last_otp: z.boolean(),
  personal_details_confirmed: z.boolean(),
  date_of_decease: ZodDateLike.nullable(),
  csp_code: ZodTrimmedString.nullable(),
  contract_ids: z.array(ZodId),
  contract_types: z.array(ZodB2bContractType),
  digital_comms_preferences_id: ZodId.nullable(),
  investor_profile: ZodEmployeeSavingsRiskProfile,
  pei_investor_profile: ZodEmployeeSavingsRiskProfile,
  products: ZodTrimmedString.array(),
  is_campaigns_blocked: z.boolean(),
  is_campaigns_blocked_regulatory: z.boolean(),
  is_user_closed_or_archived: z.boolean(),
  id_external_taiga: z.number().int().nullable(),
  is_er_eligible: z.boolean(),
});

export const ZodGetB2bEmployeesOneResponse = ZodGetB2bEmployeesOneResponseUnrefined.superRefine(
  (employee, ctx) => {
    /**
     * We allow employees to have zero contract IDs only if their account is closed
     *  this is required for the merge_employees feat
     */
    if (employee.account_status !== 'closed' && employee.contract_ids.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one contract id is required when account is active',
        path: ['contract_ids'],
      });
    }
  },
);
export type GetB2bEmployeesOneResponse = z.TypeOf<typeof ZodGetB2bEmployeesOneResponse>;

export const ZodGetB2bEmployeesResponse = z.array(
  ZodGetB2bEmployeesOneResponseUnrefined.omit({
    email_state: true,
    date_of_decease: true,
    secondary_email: true,
    secondary_email_state: true,
    has_last_otp: true,
  }),
);
export type GetB2bEmployeesResponse = z.TypeOf<typeof ZodGetB2bEmployeesResponse>;

export type EmployeeListRow = Omit<
  GetB2bEmployeesOneResponse,
  | 'digital_comms_preferences_id'
  | 'is_deceased'
  | 'date_of_decease'
  | 'eckert_status_changed_at'
  | 'last_activity_at'
  | 'email_state'
  | 'secondary_email'
  | 'secondary_email_state'
  | 'has_last_otp'
  | 'is_er_eligible'
  | 'contract_types'
>;

export const getB2bEmployeesSortColumns: (keyof EmployeeListRow)[] = [
  'id',
  'company_id',
  'company_name',
  'user_id',
  'firstname',
  'lastname',
  'email',
  'phone_number',
  'status',
  'street',
  'postalcode',
  'city',
  'country',
  'language',
  'created_at',
  'updated_at',
  'csp_code',
  'contract_ids',
  'is_activated_by_company',
  'activation_code_is_used',
  'activation_code_is_external',
];

export const ZodB2bEmployeesFilters = z
  .object({
    id: createZodArrayable(ZodId),
    query: ZodTrimmedString.optional(),
    company_id: createZodArrayable(ZodId),
    campaign_id: ZodId,
    user_id: createZodArrayable(ZodId),
    firstname: createZodArrayable(ZodTrimmedString),
    lastname: createZodArrayable(ZodTrimmedString),
    phone_number: createZodArrayable(ZodTrimmedString),
    street: createZodArrayable(ZodTrimmedString),
    postalcode: createZodArrayable(ZodTrimmedString),
    city: createZodArrayable(ZodTrimmedString),
    country: createZodArrayable(ZodCountryCode),
    language: createZodArrayable(ZodLanguageCode),
    status: createZodArrayable(ZodEmployeeStatus),
    from_created_at: createZodArrayable(ZodTrimmedString),
    to_created_at: createZodArrayable(ZodTrimmedString),
    email: createZodArrayable(ZodTrimmedString),
    company_name: createZodArrayable(ZodTrimmedString),
    account_status: createZodArrayable(ZodEmployeeAccountStatus),
    approbation_state: createZodArrayable(ZodTrimmedString),
    csp_code: createZodArrayable(ZodTrimmedString),
    is_activated_by_company: createZodArrayable(z.boolean()),
    activation_code_is_used: createZodArrayable(z.boolean()),
    nir: createZodArrayable(ZodTrimmedString),
    ntt: createZodArrayable(ZodTrimmedString),
    date_of_birth: createZodArrayable(ZodDateTimeString),
    contract_ids: createZodArrayable(ZodId),
    products: createZodArrayable(ZodB2bSavingPlanCategory),
    is_user_closed_or_archived: z.boolean(),
    registration_number: createZodArrayable(ZodTrimmedString),
  })
  .partial();

export const ZodGetB2bEmployeesQuery = buildZodBoListQueryParsed(
  ZodB2bEmployeesFilters,
  getB2bEmployeesSortColumns,
  { range: [0, 100] },
);

export type GetB2bEmployeesQuery = BoListQueryBase<
  z.TypeOf<typeof ZodB2bEmployeesFilters>,
  (typeof getB2bEmployeesSortColumns)[number]
>;

// Zod forbids `.pick()`/`.omit()`/`.partial()` on refined objects, so derive from this one
export const ZodPostCreateB2bEmployeeRequestUnrefined = z.object({
  ...ZodGetB2bEmployeesOneResponseUnrefined.pick({
    company_id: true,
    firstname: true,
    lastname: true,
    email: true,
    street: true,
    postalcode: true,
    city: true,
    country: true,
    status: true,
    place_of_birth: true,
    work_starting_date: true,
    work_ending_date: true,
    leaving_reason: true,
    social_status: true,
    tax_residence_code: true,
    retirement_date: true,
    nir: true,
    ntt: true,
    name_in_use: true,
    registration_number: true,
    role_in_company: true,
    country_of_birth: true,
    postcode_of_birth: true,
  }).shape,
  phone_number: ZodMobilePhoneNumber.nullable(),
  date_of_birth: ZodDateOfBirth.nullable(),
  is_migrated: z.boolean().optional(),
  tax_number: ZodEmployeeTaxNumber.nullable(),
  taiga_policy_holder_id: ZodId.nullable().optional(),
  contract_ids: z.array(ZodId).min(1, 'At least one contract must be selected'),
  automatic_onboarding: z.boolean(),
  user_id: ZodId.optional(),
  nationality: ZodCountryCode,
  gender: ZodEmployeeGender,
  language: ZodLanguageCode.nullable(),
  secondary_email: ZodSecureEmail.nullish(),
  csp_code: ZodTrimmedString.optional().nullable(),
  is_npai: z.boolean().nullish(),
  is_npai_mailing: z.boolean().nullish(),
  id_external: z.number().int().nullable().optional(),
});

export function withNirOrNttRequiredRefine<
  Schema extends z.ZodType<{ nir?: string | null; ntt?: string | null }>,
>(schema: Schema) {
  return schema.refine((data) => G.isNotNullable(data.nir) || G.isNotNullable(data.ntt), {
    message: 'NIR or NTT is required to create an employee',
  });
}

export const ZodPostCreateB2bEmployeeRequest = withNirOrNttRequiredRefine(
  ZodPostCreateB2bEmployeeRequestUnrefined,
);

export type PostCreateB2bEmployeeRequest = z.TypeOf<typeof ZodPostCreateB2bEmployeeRequest>;

export const ZodPostCreateB2bEmployeeResponse = ZodGetB2bEmployeesOneResponseUnrefined.omit({
  is_deceased: true,
  date_of_decease: true,
  eckert_status: true,
  eckert_status_changed_at: true,
  last_activity_at: true,
});
export type PostCreateB2bEmployeeResponse = z.TypeOf<typeof ZodPostCreateB2bEmployeeResponse>;

/**
 * Matching contribution received on another employee account of the same user, in cents. The PASS
 * limit is a single legal envelope per user, so those amounts consume the ceilings shown in `plans`.
 */
export const ZodOtherEmploymentMatchingContributions = z.object({
  b2b_employee_id: ZodId,
  b2b_company_id: ZodId,
  company_name: ZodTrimmedString,
  matching_contributions: z.partialRecord(ZodB2bSavingPlanCategory, z.number()),
});

export type OtherEmploymentMatchingContributions = z.TypeOf<
  typeof ZodOtherEmploymentMatchingContributions
>;

export const ZodGetEmployeeMatchingContributionSummary = z.object({
  id: ZodId,
  plans: z.partialRecord(
    ZodB2bSavingPlanCategory,
    ZodEmployeeSavingsMatchingContribution.omit({
      gross_amount: true,
    }).nullable(),
  ),
  other_employments_matching_contributions: z
    .array(ZodOtherEmploymentMatchingContributions)
    .default([]),
  has_custom_mc_non_estimable: z.boolean().default(false),
});

export type GetEmployeeMatchingContributionsSummaryResponse = z.TypeOf<
  typeof ZodGetEmployeeMatchingContributionSummary
>;

export const getEmployeeMatchingContributionsSummaryEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetEmployeeMatchingContributionSummary,
});

export type GetEmployeeMatchingContributionsSummaryEndpoint = GetEndpointType<
  typeof getEmployeeMatchingContributionsSummaryEndpoint
>;

export const ZodB2bEmployeeDepositBankAccount = z.object({
  account_holder_name: ZodTrimmedString,
  iban: ZodTrimmedString,
  bic: ZodTrimmedString,
  reference: ZodTrimmedString,
});

export type B2bEmployeeDepositBankAccount = z.infer<typeof ZodB2bEmployeeDepositBankAccount>;

const ZodGetB2bEmployeeDepositBankInfo = z.object({
  id: ZodId,
  deposit_bank_account: ZodB2bEmployeeDepositBankAccount.nullable(),
});

export const getB2bEmployeeDepositBankInfoEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetB2bEmployeeDepositBankInfo,
});

export type GetB2bEmployeeDepositBankInfoEndpoint = GetEndpointType<
  typeof getB2bEmployeeDepositBankInfoEndpoint
>;

const ZodPostEmployeeMatchingContributionSummaryBody = z.object({
  employee_id: ZodId,
  plan_ids: ZodId.array().min(1),
  amount: ZodAmountRequest.optional(),
  contribution_type: ZodB2bCashSource.optional(),
  allocations: z
    .array(
      z.object({
        plan_id: ZodId,
        instrument_id: ZodId.nullable(),
        amount: ZodAmountRequest.min(0),
      }),
    )
    .optional(),
});

export type PostEmployeeMatchingContributionSummaryBody = z.infer<
  typeof ZodPostEmployeeMatchingContributionSummaryBody
>;

export const getB2bEmployeesEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetB2bEmployeesQuery,
  responseBody: ZodGetB2bEmployeesResponse,
});

export type GetB2bEmployeesEndpoint = GetEndpointType<typeof getB2bEmployeesEndpoint>;

export const getB2bEmployeesIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetB2bEmployeesOneResponse,
});

export type GetB2bEmployeesIdEndpoint = GetEndpointType<typeof getB2bEmployeesIdEndpoint>;

export const getB2bEmployeesLastOtpIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: z.object({
    id: ZodId,
    code: ZodTrimmedString.nullable(),
  }),
});

export type GetB2bEmployeesLastOtpIdEndpoint = GetEndpointType<
  typeof getB2bEmployeesLastOtpIdEndpoint
>;

export const getB2bEmployeesActivationCodeIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: z.object({
    id: ZodId,
    code: ZodTrimmedString.nullable(),
  }),
});

export type GetB2bEmployeesActivationCodeIdEndpoint = GetEndpointType<
  typeof getB2bEmployeesActivationCodeIdEndpoint
>;

export const postB2bEmployeesEndpoint = endpoint({
  method: 'POST',
  body: ZodPostCreateB2bEmployeeRequest,
  responseBody: ZodPostCreateB2bEmployeeResponse,
});

export type PostB2bEmployeesEndpoint = GetEndpointType<typeof postB2bEmployeesEndpoint>;

export const ZodPatchB2bEmployeeErEligibilityRequest = z.object({
  is_er_eligible: z.boolean(),
});

export type PatchB2bEmployeeErEligibilityRequest = z.infer<
  typeof ZodPatchB2bEmployeeErEligibilityRequest
>;

export const ZodPatchB2bEmployeeErEligibilityResponse = z.object({
  employee_id: ZodId,
  user_id: ZodId,
  is_er_eligible: z.boolean(),
});

export type PatchB2bEmployeeErEligibilityResponse = z.infer<
  typeof ZodPatchB2bEmployeeErEligibilityResponse
>;

export const patchB2bEmployeeErEligibilityEndpoint = endpoint({
  method: 'PATCH',
  pathParams: ZodIDPathParam,
  body: ZodPatchB2bEmployeeErEligibilityRequest,
  responseBody: ZodPatchB2bEmployeeErEligibilityResponse,
});

export type PatchB2bEmployeeErEligibilityEndpoint = GetEndpointType<
  typeof patchB2bEmployeeErEligibilityEndpoint
>;

export const ZodEditB2bEmployeeDataRequestOne = z.discriminatedUnion('field', [
  z.object({
    field: z.enum(['firstname', 'lastname', 'postalcode', 'gender']),
    to: ZodTrimmedString,
  }),
  z.object({
    field: z.enum([
      'name_in_use',
      'role_in_company',
      'registration_number',
      'nir',
      'ntt',
      'place_of_birth',
      'postcode_of_birth',
      'street',
      'city',
    ]),
    to: ZodTrimmedString.nullable(),
  }),
  z.object({
    field: z.enum(['email', 'secondary_email']),
    to: ZodSecureEmail.nullable(),
  }),
  z.object({
    field: z.literal('retirement_date'),
    to: ZodDateLike,
  }),
  z.object({
    field: z.literal('work_starting_date'),
    to: ZodWorkStartingDate,
  }),
  z.object({
    field: z.literal('date_of_birth'),
    to: ZodDateOfBirth.nullable(),
  }),
  z.object({
    field: z.literal('work_ending_date'),
    to: ZodDateLike.nullable(),
  }),
  z.object({
    field: z.literal('status'),
    to: ZodEmployeeStatus,
  }),
  z.object({
    field: z.literal('social_status'),
    to: ZodSocialStatus,
  }),
  z.object({
    field: z.literal('tax_number'),
    to: ZodEmployeeTaxNumber.nullable(),
  }),
  z.object({
    field: z.literal('leaving_reason'),
    to: ZodLeavingReason.nullable(),
  }),
  z.object({
    field: z.literal('tax_residence_code'),
    to: ZodTaxResidenceCode,
  }),
  z.object({
    field: z.literal('phone_number'),
    to: ZodMobilePhoneNumber.nullable(),
  }),
  z.object({
    field: z.enum(['country']),
    to: ZodCountryCode,
  }),
  z.object({
    field: z.enum(['nationality', 'country_of_birth']),
    to: ZodCountryCode.nullable(),
  }),
  z.object({
    field: z.enum(['language']),
    to: ZodLanguageCode.nullable(),
  }),
  z.object({
    field: z.enum(['csp_code']),
    to: ZodTrimmedString.nullable(),
  }),
  z.object({
    field: z.literal('contract_ids'),
    to: z.array(z.number().int()).nullable(),
  }),
  z.object({
    field: z.literal('id_external_taiga'),
    to: z.number().int().nullable(),
  }),
]);

const ZodEditB2bEmployeeDataRequest = z.array(ZodEditB2bEmployeeDataRequestOne).min(1);

export type EditB2bEmployeeDataRequest = z.infer<typeof ZodEditB2bEmployeeDataRequest>;

const ZodPostB2BEmployeeEditRequest = z.object({
  employee_id: ZodId,
  changes: ZodEditB2bEmployeeDataRequest.and(z.array(z.object({ field: ZodB2bEmployeeEditField }))),
  reason: ZodTrimmedString,
});

export type PostB2BEmployeeEditRequest = z.infer<typeof ZodPostB2BEmployeeEditRequest>;

const ZodPostB2bEmployeeEditResponse = z.object({
  id: ZodId,
  state: ZodReviewedRequestState,
  created_at: z.date(),
  employee_id: ZodId,
});

export const postB2bEmployeeEditRequestEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2BEmployeeEditRequest,
  responseBody: ZodPostB2bEmployeeEditResponse,
});

export type PostB2bEmployeeEditRequestEndpoint = GetEndpointType<
  typeof postB2bEmployeeEditRequestEndpoint
>;

export const ZodAutoEditB2bEmployeeDataRequestOne = z.discriminatedUnion('field', [
  z.object({
    field: z.enum(['email', 'secondary_email']),
    to: ZodSecureEmail.nullable(),
  }),
  z.object({
    field: z.literal('phone_number'),
    to: ZodMobilePhoneNumber.nullable(),
  }),
  z.object({
    field: z.literal('postcode_of_birth'),
    to: ZodTrimmedString.nullable(),
  }),
]);

const ZodAutoEditB2bEmployeeDataRequest = z.array(ZodAutoEditB2bEmployeeDataRequestOne).min(1);

const ZodPostB2BEmployeeAutoEditRequest = z.object({
  employee_id: ZodId,
  changes: ZodAutoEditB2bEmployeeDataRequest,
  reason: ZodTrimmedString,
});

export const postB2bEmployeeAutoEditRequestEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2BEmployeeAutoEditRequest,
  responseBody: ZodPostB2bEmployeeEditResponse,
});

export type PostB2bEmployeeAutoEditRequestEndpoint = GetEndpointType<
  typeof postB2bEmployeeAutoEditRequestEndpoint
>;

export const getB2bEmployeesBalanceIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetEmployeeBalanceResponse,
});

export type GetB2bEmployeesBalanceIdEndpoint = GetEndpointType<
  typeof getB2bEmployeesBalanceIdEndpoint
>;

export const getB2bEmployeesPortfolioEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetEmployeeSavingsPortfolioQuery,
  responseBody: ZodGetEmployeeSavingsPortfolioResponse,
});

export type GetB2bEmployeesPortfolioEndpoint = GetEndpointType<
  typeof getB2bEmployeesPortfolioEndpoint
>;

export const getB2bEmployeesHeldInstrumentsIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodB2bCompanyInstrumentsResponse,
});

export type GetB2bEmployeesHeldInstrumentsIdEndpoint = GetEndpointType<
  typeof getB2bEmployeesHeldInstrumentsIdEndpoint
>;

const ZodB2bEditRequestsAction = z.enum(['APPROVED', 'REJECTED']);

const ZodPostB2bEditRequestsActionBody = z.object({
  review_request_id: ZodId,
  state: ZodB2bEditRequestsAction,
});

export type PostB2bEditRequestsActionBody = z.infer<typeof ZodPostB2bEditRequestsActionBody>;

const ZodPostB2bEditRequestsActionResponse = z.object({
  id: ZodId,
  employee_id: ZodId,
  requester_id: ZodId,
  requester_email: ZodTrimmedString.nullable(),
  reason: ZodTrimmedString,
  state: z.enum(['REQUESTED', 'APPROVED', 'REJECTED']),
  created_at: z.date(),
  updated_at: z.date(),
  reviewer_id: ZodId.nullable(),
  reviewer_email: ZodTrimmedString.nullable(),
  reviewed_at: z.date().nullable(),
  type: ZodTrimmedString,
  error_message: ZodTrimmedString.nullable(),
  assignee_id: ZodId.nullable(),
  assignee_email: ZodTrimmedString.nullable(),
});

export const postB2bEmployeesEditRequestsActionEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2bEditRequestsActionBody,
  responseBody: ZodPostB2bEditRequestsActionResponse,
});

export type PostB2bEmployeesEditRequestsActionEndpoint = GetEndpointType<
  typeof postB2bEmployeesEditRequestsActionEndpoint
>;

export const B2bEmployeesDocumentsTypeCommonValues = [
  'account_statement',
  'trade_confirmation',
  'early_withdrawal_request',
  'w8ben',
  'annual_statement_csv',
  'bank_account_statement',
  'proof_of_address',
  'account_statement_equities',
  'rib',
  'funding_source',
  'employee_savings_sepa_mandate_form_unsigned',
  'employee_savings_sepa_mandate_form_signed',
  'proof_of_identity',
  'axa_individual_transfer_bulletin',
  'axa_taiga_individual_transfer_form',
  'axa_voluntary_payment_sof_certificate',
  'b2b_account_statement_on_demand',
  'b2b_transfer_in_statement',
  'b2b_employee_welcome_letter',
  'b2b_ca_shareholder_letter',
  'b2b_ca_dividend_statement',
  'b2b_campaign_action_required_letter',
  'b2b_campaign_confirmation',
  'b2b_shareplan_operation_statement',
  // DO NOT add these documents to the list - they should not be visible anywhere
  // 'mfa_backup_codes',
  // 'proof_of_account'
] as const;

const ZodB2bEmployeesDocumentsTypeCommon = z.enum(B2bEmployeesDocumentsTypeCommonValues);
export type B2bEmployeesDocumentsTypeCommon = z.infer<typeof ZodB2bEmployeesDocumentsTypeCommon>;

export const ZodB2bEmployeeDocumentsListItem = z.object({
  id: ZodId,
  employee_id: ZodId,
  document_type: ZodB2bEmployeesDocumentsTypeCommon,
  document_url: ZodTrimmedString,
  document_name: ZodTrimmedString,
  source: ZodUserDocumentSource,
  is_archived: z.boolean(),
  task_id: ZodId.nullable(),
  created_at: z.date(),
  updated_at: z.date(),
});

export type B2bEmployeeDocumentsListItem = z.infer<typeof ZodB2bEmployeeDocumentsListItem>;

export const getB2bEmployeeDocumentsSortColumns: (keyof B2bEmployeeDocumentsListItem)[] = [
  'id',
  'employee_id',
  'document_type',
  'document_url',
  'source',
  'is_archived',
  'created_at',
  'updated_at',
];

const ZodGetB2BEmployeeDocumentsQueryFilters = z
  .object({
    document_type: createZodArrayable(ZodTrimmedString).optional(),
    id: createZodArrayable(ZodTrimmedString).optional(),
    document_url: ZodTrimmedString.optional(),
    from_created_at: ZodDateTimeString.optional(),
    to_created_at: ZodDateTimeString.optional(),
    is_archived: z.boolean().optional(),
    employee_id: createZodArrayable(ZodId),
    task_id: ZodId.optional(),
  })
  .partial();

export const ZodGetB2bEmployeeDocumentsQuery = buildZodBoListQueryParsed(
  ZodGetB2BEmployeeDocumentsQueryFilters,
  getB2bEmployeeDocumentsSortColumns,
  {
    range: [0, 100],
  },
);

export type GetB2bEmployeeDocumentsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodGetB2BEmployeeDocumentsQueryFilters>,
  (typeof getB2bEmployeeDocumentsSortColumns)[number]
>;

export const ZodB2bEmployeeDocumentsListResponse = z.array(ZodB2bEmployeeDocumentsListItem);

export type B2bEmployeeDocumentsListResponse = z.infer<typeof ZodB2bEmployeeDocumentsListResponse>;

export const getB2bEmployeesDocumentsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetB2bEmployeeDocumentsQuery,
  responseBody: ZodB2bEmployeeDocumentsListResponse,
});

export type GetB2bEmployeesDocumentsEndpoint = GetEndpointType<
  typeof getB2bEmployeesDocumentsEndpoint
>;

export const ZodB2bEmployeeDocumentDetails = ZodB2bEmployeeDocumentsListItem;

export type B2bEmployeeDocumentDetails = z.infer<typeof ZodB2bEmployeeDocumentDetails>;

export const getB2bEmployeeDocumentDetailsEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodB2bEmployeeDocumentDetails,
});

export type GetB2bEmployeeDocumentDetailsEndpoint = GetEndpointType<
  typeof getB2bEmployeeDocumentDetailsEndpoint
>;

const ZodAmount = z
  .number()
  .multipleOf(0.01)
  .transform((val) => Math.round(val * 100))
  .meta({ override: { type: 'number' } })
  .nullable();
const ZodStringOrNumber = z
  .union([ZodTrimmedString, z.number()])
  .transform(String)
  .meta({ override: { type: 'string' } });

export const ZodXlsxAllocationsData = z.array(
  z.object({
    firstname: ZodTrimmedString.optional(),
    lastname: ZodTrimmedString.optional(),
    nir: ZodStringOrNumber.pipe(ZodNIR).nullable(),
    ntt: ZodStringOrNumber.optional(),
    amount: ZodAmount,
  }),
);

export const ZodXlsxAllocationsDaysData = z.array(
  z.object({
    firstname: ZodTrimmedString.optional(),
    lastname: ZodTrimmedString.optional(),
    nir: ZodStringOrNumber.pipe(ZodNIR).nullable(),
    ntt: ZodStringOrNumber.optional(),
    cet_days: ZodAmountInDays.nullable(),
    rtt_days: ZodAmountInDays.nullable(),
    overtime_days: ZodAmountInDays.nullable(),
    paid_leave_days: ZodAmountInDays.nullable(),
    seniority_leave_days: ZodAmountInDays.nullable(),
    untaken_rest_days: ZodAmountInDays.nullable(),
  }),
);

export const ZodXlsxAllocationsVacationDaysDistributorData = z.array(
  z.object({
    firstname: ZodTrimmedString.optional(),
    lastname: ZodTrimmedString.optional(),
    nir: ZodStringOrNumber.pipe(ZodNIR).nullable(),
    ntt: ZodStringOrNumber.optional(),
    days_type: ZodB2bCampaignAllocationDayType,
    amount: ZodAmount,
    amount_in_days: ZodAmountInDays,
  }),
);

const xlsxPlanNamesValues = Object.entries(B2bCompanyPlanTypeParameters)
  .map(([_key, value]) => value.name)
  .concat('CCB')
  .concat('Versement compte bancaire');

export const ZodXlsxPlanNames = z.enum(xlsxPlanNamesValues);
type XlsxPlanNames = z.infer<typeof ZodXlsxPlanNames>;

export function getInvestingProductType(
  name: XlsxPlanNames,
): B2bSavingPlanCategory | B2bSavingPlanType | 'payout' | 'ccb' | undefined {
  if (name === 'Versement compte bancaire') {
    return 'payout';
  }

  if (name === 'CCB') {
    return 'PEE_LIBRE';
  }

  return Object.keys(B2bCompanyPlanTypeParameters).find(
    (key) =>
      B2bCompanyPlanTypeParameters[key as B2bSavingPlanCategory | B2bSavingPlanType].name === name,
  ) as B2bSavingPlanCategory | B2bSavingPlanType | undefined;
}

const xlsxRiskProfileValues = ['Équilibré', 'Prudent', 'Dynamique'] as const;

export const ZodXlsxRiskProfile = z.enum(xlsxRiskProfileValues);
export type XlsxRiskProfile = z.infer<typeof ZodXlsxRiskProfile>;
export const xlsxRiskProfileMapping: Record<XlsxRiskProfile, EmployeeSavingsRiskProfile> = {
  Équilibré: 'balanced',
  Prudent: 'careful',
  Dynamique: 'dynamic',
};

export const ZodXlsxAllocationsDataCompanyAllocatorChoice = z.array(
  z.object({
    nir: ZodStringOrNumber.pipe(ZodNIR).nullable(),
    ntt: ZodStringOrNumber.optional(),
    firstname: ZodTrimmedString.optional(),
    lastname: ZodTrimmedString.optional(),
    amount: ZodAmount.optional(),
    matching_contribution_amount: ZodAmount.optional(),
    placement_support: ZodTrimmedString.optional(),
    plan: ZodTrimmedString.optional(),
  }),
);

export const ZodUpdateB2bEmployeePayload = z.object({
  ...ZodPostCreateB2bEmployeeRequestUnrefined.omit({
    company_id: true,
    automatic_onboarding: true,
    is_migrated: true,
  }).partial().shape,
  user_id: ZodId,
  employee_id: ZodId,
  secondary_email: ZodSecureEmail.nullish(),
  id_external_taiga: z.number().int().nullable().optional(),
});

export type UpdateB2bEmployeePayload = z.infer<typeof ZodUpdateB2bEmployeePayload>;
